// React error boundary — wraps route content and reports to Sentry via captureErr.
// V1 is vanilla JS with a window error handler; V2 needs a React boundary so
// render-time crashes don't blank the whole shell.

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { captureErr } from '../lib/sentry'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
  info: ErrorInfo | null
}

// Detect chunk-load / dynamic-import failures. These happen after a deploy
// when the cached index.html references a bundle hash that's no longer on
// the CDN — React.lazy() errors with one of these messages. A one-shot
// reload fetches the new index.html + new hashes; sessionStorage guard
// prevents an infinite reload loop if the failure is something else.
const CHUNK_ERR = /Loading chunk|Loading CSS chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false
  const msg = err instanceof Error ? err.message : String(err)
  return CHUNK_ERR.test(msg)
}

function autoReloadOnce(): boolean {
  const KEY = 'jsp.chunkReloadAt'
  try {
    const last = Number(sessionStorage.getItem(KEY) || '0')
    if (Date.now() - last < 15_000) return false
    sessionStorage.setItem(KEY, String(Date.now()))
  } catch { /* storage blocked — still try to reload */ }
  window.location.reload()
  return true
}

// Vite emits this on module-preload failures; catching it earlier than the
// React boundary keeps the app from painting the crash card at all.
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (e: Event) => {
    e.preventDefault()
    autoReloadOnce()
  })
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    if (isChunkLoadError(e.reason)) {
      e.preventDefault()
      autoReloadOnce()
    }
  })
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Stale-bundle recovery — swap cached index.html for the fresh one.
    if (isChunkLoadError(error)) {
      if (autoReloadOnce()) return
    }
    this.setState({ info })
    captureErr(error, { where: 'ErrorBoundary', componentStack: info.componentStack ?? '' })
  }

  handleReload = () => {
    try { sessionStorage.removeItem('jsp.chunkReloadAt') } catch { /* noop */ }
    window.location.reload()
  }

  render() {
    if (!this.state.error) return this.props.children

    const err = this.state.error
    return (
      <div
        role="alert"
        style={{
          margin: '2rem auto',
          maxWidth: 560,
          padding: '1.5rem',
          background: 'var(--surface, #1e293b)',
          border: '1px solid var(--border, #334155)',
          borderRadius: 'var(--radius, 8px)',
          color: 'var(--text, #f8fafc)',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <h2 style={{ margin: 0, marginBottom: 10, fontSize: 18, fontWeight: 700 }}>
          Something went wrong
        </h2>
        <p style={{ marginTop: 0, marginBottom: 16, color: 'var(--text-muted, #94a3b8)', fontSize: 14 }}>
          The app hit an unexpected error. Your data is safe — reload to get back in.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            padding: '8px 18px',
            borderRadius: 6,
            border: 'none',
            background: 'var(--accent, #22c55e)',
            color: 'var(--accent-contrast, #0f172a)',
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
        <details style={{ marginTop: 16, fontSize: 12, color: 'var(--text-muted, #94a3b8)' }}>
          <summary style={{ cursor: 'pointer' }}>Error details</summary>
          <pre
            style={{
              marginTop: 8,
              padding: 10,
              background: 'var(--bg, #0f172a)',
              border: '1px solid var(--border, #334155)',
              borderRadius: 4,
              maxHeight: 240,
              overflow: 'auto',
              fontFamily: 'var(--mono, ui-monospace, monospace)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {err.message}
            {err.stack ? `\n\n${err.stack}` : ''}
            {this.state.info?.componentStack ? `\n\nComponent stack:${this.state.info.componentStack}` : ''}
          </pre>
        </details>
      </div>
    )
  }
}
